<LayoutMain class="personal-detail">
  <!-- Unify top interface across personals: breadcrumb on all pages -->
  <PersonalsTopBar :showCreateCTA="true" />

  <PageHeader :title="t('personals.adDetail')" :subtitle="ad ? ad.title : ''">
    <template #actions>
      <button variant="ghost" size="sm" @click="goBack">
        <ArrowLeft :size="16" />
        {{ t('common.actions.back') }}
      </button>
    </template>
  </PageHeader>

  <!-- Loading State -->
  <div v-if="isLoading && !ad" class="flex justify-center py-12">
    <LoadingSpinner size="lg" />
  </div>

  <!-- Error Display -->
  <ErrorDisplay v-else-if="error" :error="error" @retry="loadAd" class="mb-6" />

  <!-- Ad Content -->
  <div v-else-if="ad" class="space-y-6">
    <!-- Success Message -->
    <div v-if="replySuccess" class="success-message">{{ replySuccess }}</div>

    <!-- Main Ad Card -->
    <Card variant="glass" class="ad-card">
      <template #header>
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1">
            <Heading level="1" class="text-2xl font-bold text-forest-primary mb-2">
              {{ ad.title }}
            </Heading>

            <div class="flex items-center gap-3 flex-wrap">
              <div class="flex items-center gap-1 text-neutral-500">
                <Clock :size="14" />
                <Text variant="caption">
                  {{ t('personals.posted') }} {{ formatTimeAgo(ad.created_at) }}
                </Text>
              </div>

              <div class="flex items-center gap-1 text-accent-coral">
                <MessageCircle :size="14" />
                <Text variant="caption" class="font-medium">
                  {{ t('personals.replies', { count: ad.reply_count }) }}
                </Text>
              </div>
            </div>
          </div>
        </div>
      </template>

      <div class="space-y-4">
        <Text variant="body" class="ad-content text-neutral-700">
          {{ ad.body || 'No body content available' }}
        </Text>

        <div class="flex items-center justify-between pt-4 border-t border-neutral-200">
          <Text variant="caption" class="text-neutral-500">
            {{ t('personals.posted') }} {{ formatPostDate(ad.created_at) }}
          </Text>

          <!-- Hide reply action if viewer is the poster or has already replied -->
          <div v-if="!showReplyForm && ad && !ad.is_mine" class="flex items-center gap-2">
            <button
              v-if="!userHasReplied"
              variant="primary"
              @click="handleShowReplyForm"
              :disabled="!canReply"
            >
              <MessageCircle :size="16" />
              {{ t('personals.reply') }}
            </button>
            <button
              v-else
              variant="ghost"
              size="sm"
              class="group-hover:scale-105 transition-all duration-300"
              style="color: var(--rogue-vibrant-green)"
              @click="$router.push({ name: 'my-replies', query: { highlight: lastReplyId } })"
            >
              <Eye class="w-3 h-3 mr-1" />
              {{ t('personals.viewYourReply') }}
            </button>
          </div>
        </div>
      </div>
    </Card>

    <!-- Rate Limit Warning -->
    <div v-if="!canReply && !showReplyForm" class="error-message">
      <div class="flex items-center gap-2">
        <AlertTriangle :size="16" />
        <Text variant="body"> {{ t('personals.replyLimitReached') }} </Text>
      </div>
    </div>

    <!-- Replies Section (only visible to poster) -->
    <div v-if="ad && ad.is_mine" class="replies-section">
      <div class="flex items-center gap-2 mb-6">
        <MessageCircle :size="20" style="color: var(--rogue-vibrant-green)" />
        <Heading level="2" class="text-lg font-semibold" style="color: var(--rogue-deep-forest)">
          {{ t('personals.repliesReceived', { count: replies.length }) }}
        </Heading>
      </div>

      <!-- Empty Replies State -->
      <div v-if="!replies.length && !isLoading" class="empty-replies-state">
        <Card variant="glass" class="p-8 text-center">
          <div
            class="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style="
              background: linear-gradient(
                135deg,
                var(--overlay-green-light),
                var(--overlay-coral-light)
              );
            "
          >
            <MessageCircle :size="24" style="color: var(--rogue-deep-forest)" />
          </div>
          <Text variant="body" class="text-neutral-600 mb-2">
            {{ t('personals.noRepliesYet', 'No replies yet') }}
          </Text>
          <Text variant="caption" class="text-neutral-500">
            {{ t('personals.noRepliesDescription') }}
          </Text>
        </Card>
      </div>

      <!-- Replies List -->
      <div v-else class="space-y-4">
        <Card v-for="reply in replies" :key="reply.id" variant="elevated" class="reply-item">
          <template #header>
            <div class="flex items-start gap-3">
              <!-- User Avatar -->
              <div class="user-avatar-container">
                <img
                  v-if="reply.profiles?.thumbnail_url"
                  :src="getAvatarImage(reply.profiles.thumbnail_url)"
                  :alt="reply.profiles?.name || 'User'"
                  class="user-avatar"
                />
                <div v-else class="user-avatar user-avatar-placeholder">
                  <User :size="20" style="color: var(--rogue-warm-cream)" />
                </div>
              </div>

              <!-- User Info -->
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1">
                  <button @click="viewProfile(reply.responder_id)" class="user-name-link">
                    {{ reply.profiles?.name || t('personals.anonymousUser', 'Anonymous User') }}
                  </button>
                  <Text variant="caption" class="text-neutral-500">
                    {{ formatTimeAgo(reply.created_at) }}
                  </Text>
                </div>

                <Text
                  v-if="reply.profiles?.bio"
                  variant="caption"
                  class="text-neutral-600 line-clamp-1"
                >
                  {{ reply.profiles.bio }}
                </Text>
              </div>
            </div>
          </template>

          <!-- Reply Message -->
          <div class="reply-content">
            <Text
              variant="body"
              class="whitespace-pre-wrap leading-relaxed"
              style="color: var(--rogue-deep-forest)"
            >
              {{ reply.message }}
            </Text>
          </div>

          <!-- Reply Actions -->
          <template #footer>
            <div class="flex justify-end">
              <button
                variant="ghost"
                size="sm"
                @click="viewProfile(reply.responder_id)"
                class="view-profile-btn"
              >
                <Eye :size="14" class="mr-1" />
                {{ t('personals.viewProfile', 'View Profile') }}
              </button>
            </div>
          </template>
        </Card>
      </div>
    </div>
  </div>
  <!-- Reply Form (Sticky Bottom) -->
  <div v-if="showReplyForm" class="reply-form">
    <Card variant="outline" class="border-accent-coral">
      <template #header>
        <div class="flex items-center justify-between">
          <Heading level="3" class="text-lg font-semibold text-forest-primary">
            {{ t('personals.writeReply') }}
          </Heading>
          <button variant="ghost" size="sm" @click="handleCancelReply">
            {{ t('common.actions.cancel') }}
          </button>
        </div>
      </template>

      <div class="space-y-4">
        <!-- Error Message -->
        <div v-if="replyError" class="error-message">{{ replyError }}</div>

        <!-- Reply Textarea -->
        <div>
          <textarea
            v-model="replyForm.message"
            :placeholder="t('personals.replyPlaceholder')"
            :error="replyErrors.message"
            rows="4"
            class="w-full"
            :disabled="replyForm.isSubmitting"
            @input="validateReply"
          />

          <div class="flex items-center justify-between mt-2">
            <Text
              variant="caption"
              :class="{
                'character-counter': true,
                'warning': characterCount > 400,
                'danger': characterCount > 500
              }"
            >
              {{ characterCount }} / 500 {{ t('common.characters') }}
            </Text>

            <div class="text-red-500" v-if="replyErrors.message">
              <Text variant="caption">{{ replyErrors.message }}</Text>
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="flex justify-end">
          <button
            variant="primary"
            @click="handleSubmitReply"
            :disabled="replyForm.isSubmitting || !replyForm.message.trim()"
          >
            <LoadingSpinner v-if="replyForm.isSubmitting" size="sm" class="mr-2" />
            <Send v-else :size="16" class="mr-2" />
            {{ replyForm.isSubmitting ? t('common.actions.sending') : t('common.actions.send') }}
          </button>
        </div>
      </div>
    </Card>
  </div>
</LayoutMain>
